'use client';

import { useMutation, useQuery } from '@apollo/client';
import { Popover, Space, Stack, Text } from '@mantine/core';
import { useScrollIntoView } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import ArrowDropDown from '@material-design-icons/svg/filled/arrow_drop_down.svg';
import ChevronLeft from '@material-design-icons/svg/filled/chevron_left.svg';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Animate,
  BoostSwayModal,
  ColumnarLayout,
  ErrorPage,
  InfluenceTargetType,
  IssueActionButtons,
  IssueHeader,
  IssueLeaderProfile,
  IssuePageInfluenceTargetSection,
  IssuePageInformationSection,
  LoginModal,
  Warning,
} from '@/components';
import {
  IssuePageViewer,
  useHeader,
  useIssuePage,
  usePageTitle,
} from '@/contexts';
import {
  Entity_InfoFragment,
  IssuesPage_CheckSupporterDocument,
  IssuesPage_CreateIssueDocument,
  IssuesPage_GetIssueDocument,
  IssuesPage_InsertIssueMediaRelDocument as InsertIssueMediaRelDocument,
  IssuesPage_IssueFragment as Issue,
  IssuesPage_LeaderDocument,
  IssuesPage_MakeIssueActiveDocument,
  IssuesPage_MakeIssueInactiveDocument,
  IssuesPage_UnvoteDocument,
  IssuesPage_UpdateIssueDocument,
  IssuesPage_VoteWithMeDocument,
  ProfileEntityPage_GetEntityQueryDocument as GetEntityQuery,
  ProfilePhotoUpload_InsertMediaDocument as InsertMediaMutation,
} from '@/graphql';
import { useAuth, useIssueData } from '@/hooks';
import { isEqual, logger } from '@/utils';

import gatherAnimation from '../../animations/gather.json';
import moveAnimation from '../../animations/move_forward.json';

import styles from './IssuePageContent.module.css';

export interface IssueFormData {
  topic: string;
  slogan: string;
  location: string;
  summary: string;
  imageUrls: string[];
  influenceTargets: InfluenceTargetType[];
}

export const DEFAULT_ISSUE_FORM_DATA: IssueFormData = {
  topic: '',
  slogan: '',
  location: '',
  summary: '',
  imageUrls: [],
  influenceTargets: [],
};

// LocalStorage keys
export const ISSUE_FORM_DATA_KEY = 'sway_page_form_data';
export const ISSUE_EDIT_MODE_KEY = 'sway_page_edit_mode';

export const saveToLocalStorage = <T,>(key: string, data: T): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    logger.error('Failed to save to localStorage', {
      error: error instanceof Error ? error.message : String(error),
      component: 'IssuePageContent',
      key,
    });
  }
};

export const getFromLocalStorage = <T,>(key: string, defaultValue: T): T => {
  if (typeof window === 'undefined') return defaultValue;
  try {
    const storedValue = localStorage.getItem(key);
    return storedValue ? (JSON.parse(storedValue) as T) : defaultValue;
  } catch (error) {
    logger.error('Failed to retrieve from localStorage', {
      error: error instanceof Error ? error.message : String(error),
      component: 'IssuePageContent',
      key,
    });
    return defaultValue;
  }
};

const clearLocalStorageData = (keys: string[]): void => {
  if (typeof window === 'undefined') return;
  try {
    keys.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    logger.error('Failed to clear localStorage', {
      error: error instanceof Error ? error.message : String(error),
      component: 'IssuePageContent',
      keys,
    });
  }
};

interface MappedInfluenceTarget {
  issueId?: string;
  title: string;
  subtitle?: string | null;
  influenceTarget: {
    data: {
      civicEngineId: string;
      jurisdiction: {
        data: {
          geoid: string;
        };
      };
      offices?: {
        data: { name?: string }[];
      };
      measures?: {
        data: { title?: string; summary?: string | null }[];
      };
    };
  };
}

interface IssuePageProps {
  initialIssue?: Issue;
  initialIssueId?: string;
}

const IssuePageContent: React.FC<IssuePageProps> = ({
  initialIssue,
  initialIssueId,
}) => {
  const {
    currentEntityId,
    currentPersonId,
    isCurrentUserVerified,
    isLoading: isAuthLoading,
  } = useAuth();

  const { issue, refetch: refetchIssue } = useIssueData(
    initialIssueId,
    currentEntityId || undefined,
    initialIssue,
  );

  const issueId = issue?.id;
  const entityId = currentEntityId;
  const personId = currentPersonId;

  const {
    pageState,
    setPageState,
    isCreateModeWithRequiredInfo,
    isCreateModeWithNoInfo,
    isEditMode,
    isViewModeAsOwner,
    isViewModeAsSupporter,
    isPreviewModeFromCreating,
    isPreviewModeFromEditing,
    getIsCreateMode,
    getIsFullEditMode,
    getIsStaticMode,
    getIsPreviewMode,
    getIsViewMode,
    getIsVoteMode,
    isViewModeWithLoginModal,
    isViewModeWithBoostSwayModal,
  } = useIssuePage();

  const { setPageTitle } = usePageTitle();
  const {
    setShowSloganInput,
    secondaryTitle,
    setIsSloganEditable,
    setIsPreviewSlogan,
    openInfoModal,
    setRefreshSlogan,
  } = useHeader();

  const router = useRouter();
  const searchParams = useSearchParams();

  // Initialize form data from localStorage or default values
  const [formData, setFormData] = useState<IssueFormData>(() =>
    getFromLocalStorage<IssueFormData>(
      ISSUE_FORM_DATA_KEY,
      DEFAULT_ISSUE_FORM_DATA,
    ),
  );

  // Initialize influence targets from the form data
  const [influenceTargets, setInfluenceTargets] = useState<
    InfluenceTargetType[]
  >(formData.influenceTargets || []);

  const [issueHasInfluenceTargets, setIssueHasInfluenceTargets] =
    useState<boolean>(false);

  const { data: leaderData } = useQuery(IssuesPage_LeaderDocument, {
    variables: { entityId: entityId },
    skip: !entityId,
  });

  // QR Code Modal state
  const issueUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/profiles/${leaderData?.entitiesByPk?.id || entityId}/topics/${issueId}`
      : `${process.env.NEXT_PUBLIC_APP_URL}/profiles/${leaderData?.entitiesByPk?.id || entityId}/topics/${issueId}`;

  const { data: supporterData, refetch: refetchSupporterCheck } = useQuery(
    IssuesPage_CheckSupporterDocument,
    {
      variables: {
        issueId: issueId,
        supporterId: entityId,
      },
      skip: !entityId || !issueId,
    },
  );

  const isSupporterOfCurrentIssue = useMemo(
    () => (supporterData?.issueSupporterRels?.length ?? 0) > 0,
    [supporterData],
  );

  const [updateIssue] = useMutation(IssuesPage_UpdateIssueDocument);
  const [createIssue] = useMutation(IssuesPage_CreateIssueDocument, {
    refetchQueries: [
      {
        query: GetEntityQuery,
        variables: { entityId },
      },
    ],
  });
  const [insertMedia] = useMutation(InsertMediaMutation);
  const [insertIssueMediaRel] = useMutation(InsertIssueMediaRelDocument);
  const [voteWithMe] = useMutation(IssuesPage_VoteWithMeDocument);
  const [unvoteFromIssue] = useMutation(IssuesPage_UnvoteDocument);
  const [makeIssueInactive] = useMutation(
    IssuesPage_MakeIssueInactiveDocument,
    {
      refetchQueries: [
        {
          query: GetEntityQuery,
          variables: { entityId },
        },
      ],
    },
  );
  const [makeIssueActive] = useMutation(IssuesPage_MakeIssueActiveDocument, {
    refetchQueries: [
      {
        query: IssuesPage_GetIssueDocument,
        variables: { id: issueId },
      },
    ],
  });

  const displayLeader = (
    !getIsCreateMode() && !isPreviewModeFromCreating(pageState)
      ? issue?.leader || {}
      : leaderData?.entitiesByPk || {}
  ) as Entity_InfoFragment;

  const [buttonText, setButtonText] = useState<string>('');

  const prevFormDataRef = useRef<IssueFormData | null>(null);

  const [publishing, setPublishing] = useState(false);
  const [supporting, setSupporting] = useState(false);
  const [pendingRoute, setPendingRoute] = useState<string | null>(null);

  useEffect(() => {
    if (isPreviewModeFromCreating(pageState)) {
      setButtonText('PUBLISH');
    } else if (isPreviewModeFromEditing(pageState)) {
      setButtonText('SAVE');
    } else if (!getIsStaticMode()) {
      setButtonText('PREVIEW');
    } else if (isViewModeAsOwner(pageState)) {
      setButtonText('SHARE');
    } else if (isViewModeAsSupporter(pageState)) {
      setButtonText('REMOVE YOUR VOTE');
    } else {
      setButtonText('VOTE WITH ME');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState]);

  // USE EFFECTS FOR EDITING SLOGAN
  useEffect(() => {
    if (getIsFullEditMode()) {
      setIsSloganEditable(true);
    } else if (getIsCreateMode()) {
      setIsSloganEditable(true);
    } else {
      setIsSloganEditable(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getIsFullEditMode, getIsCreateMode]);

  useEffect(() => {
    if (isEditMode(pageState) || isCreateModeWithRequiredInfo(pageState)) {
      setIsSloganEditable(true);
      setPageTitle('', issue?.slogan || '');
    } else if (isCreateModeWithNoInfo(pageState)) {
      setShowSloganInput(true);
      setPageTitle('', '');
    } else if (getIsPreviewMode()) {
      setIsPreviewSlogan(true);
      setPageTitle('', formData.slogan || '');
    } else {
      setIsSloganEditable(false);
      setPageTitle('', issue?.slogan || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageState,
    isCreateModeWithNoInfo,
    isCreateModeWithRequiredInfo,
    getIsPreviewMode,
    formData,
    issue,
    isEditMode,
  ]);

  // Reset on unmount
  useEffect(() => {
    return () => {
      setIsSloganEditable(false);
      setShowSloganInput(false);
    };
  }, [setIsSloganEditable, setShowSloganInput]);

  // 1. Handle page mode and viewer state based on entity and issue
  useEffect(() => {
    if (issue && getIsViewMode() && !getIsVoteMode()) {
      let newViewerState = 'public';
      if (!entityId) {
        newViewerState = 'public';
        if (!isAuthLoading) {
          openInfoModal();
        }
      } else if (issue.leader.id === entityId) {
        newViewerState = 'owner';
      } else if (
        // TODO this needs to be refactored
        'viewer' in pageState &&
        pageState.viewer === 'supporter'
      ) {
        newViewerState = 'supporter';
      } else {
        newViewerState = isSupporterOfCurrentIssue ? 'supporter' : 'user';
      }

      // Only update state if it's different than the current state
      if (
        pageState.mode !== 'viewing' ||
        (pageState.mode === 'viewing' &&
          'viewer' in pageState &&
          pageState.viewer !== newViewerState)
      ) {
        setPageState({
          mode: 'viewing',
          viewer: newViewerState as IssuePageViewer,
        });
      }
    } else if (getIsCreateMode()) {
      if (pageState.mode !== 'creating') {
        setPageState({ mode: 'creating', hasTopic: false });
      } else if (
        pageState.mode === 'creating' &&
        !pageState.hasTopic &&
        formData.topic
      ) {
        setPageState({ mode: 'creating', hasTopic: true });
      }
    }
  }, [
    issue,
    entityId,
    pageState,
    setPageState,
    getIsViewMode,
    getIsCreateMode,
    isSupporterOfCurrentIssue,
    getIsVoteMode,
    formData,
    isAuthLoading,
    openInfoModal,
  ]);

  // 2a. Handle form data population when issue data is available in view mode
  useEffect(() => {
    if (issue && getIsViewMode()) {
      // Get all media links from the issue's media relations
      const mediaLinks =
        issue.issueMediaRels?.map((rel) => rel.medium.link) || [];

      const newFormData = {
        topic: issue.topic || '',
        slogan: issue.slogan || '',
        location: issue.location || '',
        summary: issue.summary || '',
        imageUrls: mediaLinks,
        influenceTargets: influenceTargets,
      };

      if (!isEqual(prevFormDataRef.current, newFormData)) {
        setFormData(newFormData);
        prevFormDataRef.current = newFormData;
      }
      setPageTitle('', issue.slogan);
      setIsSloganEditable(getIsFullEditMode());
    }

    return () => setPageTitle('', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue, getIsViewMode, getIsFullEditMode, influenceTargets]);

  // 2b. Handle form data population from localStorage in create mode
  useEffect(() => {
    if (getIsCreateMode() || isEditMode(pageState)) {
      // Use data from localStorage if it exists
      const storedFormData = getFromLocalStorage<IssueFormData>(
        ISSUE_FORM_DATA_KEY,
        DEFAULT_ISSUE_FORM_DATA,
      );

      // Only update if we have stored data with content AND it's different from previous value
      const hasContent =
        storedFormData && (storedFormData.topic || storedFormData.slogan);

      if (hasContent && !isEqual(prevFormDataRef.current, storedFormData)) {
        setFormData(storedFormData);
        if (storedFormData.slogan !== prevFormDataRef.current?.slogan) {
          setRefreshSlogan(true);
        }
        prevFormDataRef.current = storedFormData;

        // Update page state based on topic existence
        if (storedFormData.topic && !isEditMode(pageState)) {
          setPageState({ mode: 'creating', hasTopic: true });
        }
      }

      setPageTitle('', storedFormData.slogan || '');
    }

    return () => setPageTitle('', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState, influenceTargets]);

  // Handle influence targets
  useEffect(() => {
    if (getIsViewMode() && issue?.issueInfluenceTargetRels) {
      // In view mode, use the issue's influence targets
      setIssueHasInfluenceTargets(
        (issue.issueInfluenceTargetRels.length ?? 0) > 0 ||
          (influenceTargets?.length ?? 0) > 0,
      );
    } else if ((getIsCreateMode() || getIsFullEditMode()) && !issue) {
      // In create or edit mode without an issue, use the targets from form data
      const storedFormData = getFromLocalStorage<IssueFormData>(
        ISSUE_FORM_DATA_KEY,
        DEFAULT_ISSUE_FORM_DATA,
      );

      if (
        storedFormData.influenceTargets?.length > 0 &&
        influenceTargets.length === 0
      ) {
        setInfluenceTargets(storedFormData.influenceTargets);
      }

      setIssueHasInfluenceTargets(
        influenceTargets.length > 0 ||
          (storedFormData.influenceTargets?.length ?? 0) > 0,
      );
    } else {
      // Default case
      setIssueHasInfluenceTargets(
        (issue?.issueInfluenceTargetRels?.length ?? 0) > 0 ||
          (influenceTargets?.length ?? 0) > 0,
      );
    }
  }, [
    issue,
    influenceTargets,
    getIsViewMode,
    getIsCreateMode,
    getIsFullEditMode,
  ]);

  // --- SEARCH PARAMS 1: On mount, check for mode=edit in search params and set edit mode ---
  useEffect(() => {
    if (searchParams?.get('mode') === 'edit' && !isEditMode(pageState)) {
      setPageState({ mode: 'editing' });
    }
    // Only run on mount or when searchParams changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // TODO Move to `utils`.
  const mapToInfluenceTargets = useCallback(
    (inputData: InfluenceTargetType[]): MappedInfluenceTarget[] => {
      return inputData.map((item) => ({
        ...(!getIsCreateMode() && issueId ? { issueId: issueId } : {}),
        title: item.title,
        subtitle: item.subtitle || null,
        influenceTarget: {
          data: {
            civicEngineId: item.civicEngineId,
            jurisdiction: {
              data: {
                geoid: item.geoId, // Assuming influenceTargetId is the geoid
                // name: item.jurisdictionName, // TODO currently not supported
              },
            },
            ...(item.type === 'offices' && {
              offices: {
                data: [{ name: item.officeName }],
              },
            }),
            ...(item.type === 'measures' && {
              measures: {
                data: [
                  { title: item.measureTitle, summary: item.measureSummary },
                ],
              },
            }),
          },
        },
      }));
    },
    [getIsCreateMode, issueId],
  );

  const handleEditIssue = useCallback(() => {
    setIsPreviewSlogan(false); // need to explicitly reset this when toggling through edit, preview, and view

    // Save the current form data to localStorage when entering edit mode
    const updatedFormData = { ...formData, influenceTargets };
    saveToLocalStorage(ISSUE_FORM_DATA_KEY, updatedFormData);
    saveToLocalStorage(ISSUE_EDIT_MODE_KEY, true);

    setPageState({ mode: 'editing' });

    // Add mode=edit to the URL
    const url = new URL(window.location.href);
    url.searchParams.set('mode', 'edit');
    router.replace(url.pathname + url.search);

    return;
  }, [formData, influenceTargets, router, setIsPreviewSlogan, setPageState]);

  const handleSetIssueInactive = useCallback(() => {
    if (issue?.isActive) {
      makeIssueInactive({
        variables: {
          id: issueId,
        },
      });
      router.push(`/profiles/${entityId}`);
    } else {
      makeIssueActive({
        variables: {
          id: issueId,
        },
      });
      refetchIssue();
    }
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, issueId, router, issue?.isActive]);

  const handleBackButton = useCallback(() => {
    if (getIsPreviewMode()) {
      if (isPreviewModeFromCreating(pageState)) {
        setPageState({ mode: 'creating', hasTopic: true });
      } else {
        setPageState({ mode: 'editing' });
      }
      setIsPreviewSlogan(false);
      setIsSloganEditable(true);
    } else if (getIsCreateMode()) {
      // Ask user if they want to save their draft before leaving
      const confirmLeave = window.confirm(
        'Do you want to leave this page? Your changes will be lost. Click OK to exit, or Cancel to keep editing.',
      );

      if (confirmLeave) {
        // User chose to discard, clear localStorage
        clearLocalStorageData([ISSUE_FORM_DATA_KEY, ISSUE_EDIT_MODE_KEY]);
        router.push(`/me`);
      }
    } else {
      setPageState({ mode: 'viewing', viewer: 'owner' });
    }
  }, [
    getIsCreateMode,
    getIsPreviewMode,
    isPreviewModeFromCreating,
    pageState,
    router,
    setIsPreviewSlogan,
    setIsSloganEditable,
    setPageState,
  ]);

  const handleVoteWithMe = useCallback(
    async (shouldSetPageState = true) => {
      setSupporting(true);
      await voteWithMe({
        variables: {
          issueId: issueId,
          supporterId: entityId,
        },
      });

      // Only set page state if shouldSetPageState is true
      if (shouldSetPageState) {
        setPageState({
          mode: 'viewing',
          viewer: 'supporter',
          modal: 'boostSway',
        });
      }

      // Refetch the issue data to update the supporter count
      if (refetchIssue) {
        await refetchIssue();
      }
    },
    [voteWithMe, issueId, entityId, refetchIssue, setPageState],
  );

  const handleAction = useCallback(async () => {
    if (isViewModeAsOwner(pageState)) {
      // Open QR code modal for sharing
      modals.openContextModal({
        modal: 'share',
        innerProps: {
          issueUrl,
          issueTopic: formData.topic,
          issueLeader: displayLeader.displayNameShort || '',
        },
      });

      return;
    }

    if (getIsViewMode()) {
      // User is not logged in, so we need to show the login modal
      if (!entityId) {
        setPageState({ mode: 'viewing', viewer: 'public', modal: 'login' });
        return;
      }

      try {
        // Unvote
        if (isViewModeAsSupporter(pageState)) {
          await unvoteFromIssue({
            variables: {
              issueId: issueId,
              supporterId: entityId,
            },
          });

          await refetchSupporterCheck();

          if (refetchIssue) {
            await refetchIssue();
          }
          logger.info('Successfully removed vote from issue', {
            component: 'IssuePage',
            issueId: issueId,
          });

          setPageState({ mode: 'viewing', viewer: 'user' });
        } else {
          // User is signed in and voting
          await handleVoteWithMe(true);
        }
      } catch (error) {
        logger.error(
          `Error ${isViewModeAsSupporter(pageState) ? 'removing vote from' : 'voting for'} issue`,
          {
            error: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined,
            component: 'IssuePage',
          },
        );
      }
      return;
    } else if (getIsPreviewMode()) {
      setPublishing(true);
      try {
        // Publish the issue
        // Get latest form data from localStorage
        const savedFormData = getFromLocalStorage<IssueFormData>(
          ISSUE_FORM_DATA_KEY,
          formData,
        );
        const savedInfluenceTargets =
          savedFormData.influenceTargets || influenceTargets;

        if (isPreviewModeFromCreating(pageState)) {
          if (!entityId) {
            logger.error('No leader ID available for issue creation', {
              component: 'IssuePage',
            });
            return;
          }

          const trimmedTopic = savedFormData.topic.trim();

          // Create the issue with data from localStorage
          const { data } = await createIssue({
            variables: {
              topic: trimmedTopic,
              slogan: secondaryTitle || savedFormData.slogan,
              location: savedFormData.location,
              summary: savedFormData.summary,
              leaderId: entityId,
              influenceTargets: mapToInfluenceTargets(savedInfluenceTargets),
            },
          });

          const issueId = data?.insertIssuesOne?.id;
          if (!issueId) {
            logger.error('Failed to create issue', {
              component: 'IssuePage',
            });
            return;
          }

          // Insert all media URLs sequentially, using latest from localStorage
          const mediaIds = [];
          if (savedFormData.imageUrls.length > 0) {
            for (const imageUrl of savedFormData.imageUrls) {
              try {
                const mediaResult = await insertMedia({
                  variables: {
                    photoUrl: imageUrl,
                  },
                });

                const mediaId = mediaResult.data?.insertMedia?.id;
                if (mediaId) {
                  mediaIds.push(mediaId);
                }
              } catch (error) {
                logger.error(`Failed to insert media for URL: ${imageUrl}`, {
                  error: error instanceof Error ? error.message : String(error),
                  component: 'IssuePage',
                });
              }
            }
          }

          // Create issue media relationships for each media ID
          if (mediaIds.length > 0) {
            try {
              await Promise.all(
                mediaIds.map((mediaId) =>
                  insertIssueMediaRel({
                    variables: {
                      issueId,
                      mediaId,
                    },
                  }),
                ),
              );

              logger.debug(
                `Successfully created ${mediaIds.length} media relations for issue ${issueId}`,
                {
                  component: 'IssuePage',
                },
              );
            } catch (error) {
              logger.error(
                `Failed to create media relations for issue ${issueId}`,
                {
                  error: error instanceof Error ? error.message : String(error),
                  component: 'IssuePage',
                },
              );
            }
          }

          // Clear localStorage data after successful creation
          clearLocalStorageData([ISSUE_FORM_DATA_KEY, ISSUE_EDIT_MODE_KEY]);
          setPendingRoute(`/profiles/${entityId}/topics/${issueId}`);
          return;
        } else {
          // Handle media updates - compare current and previous lists of media
          const currentMediaUrls =
            issue?.issueMediaRels?.map((rel) => rel.medium.link) || [];

          // Get the latest image URLs from localStorage

          const mediaToAdd = savedFormData.imageUrls.filter(
            (url) => !currentMediaUrls.includes(url),
          );

          const newMediaIds = [];

          // Insert new media
          if (mediaToAdd.length > 0) {
            for (const imageUrl of mediaToAdd) {
              try {
                const mediaResult = await insertMedia({
                  variables: {
                    photoUrl: imageUrl,
                  },
                });

                const mediaId = mediaResult.data?.insertMedia?.id;
                if (mediaId) {
                  newMediaIds.push(mediaId);
                }
              } catch (error) {
                logger.error(`Failed to insert media for URL: ${imageUrl}`, {
                  error: error instanceof Error ? error.message : String(error),
                  component: 'IssuePage',
                });
              }
            }
          }

          // First update the issue basic info
          await updateIssue({
            variables: {
              id: issueId,
              slogan: secondaryTitle || savedFormData.slogan,
              location: savedFormData.location,
              summary: savedFormData.summary,
              influenceTargets: mapToInfluenceTargets(savedInfluenceTargets),
            },
          });

          // Then create issue_media_rels for any new media
          if (newMediaIds.length > 0) {
            try {
              // Create issue media relationships for each new media ID
              await Promise.all(
                newMediaIds.map((mediaId) =>
                  insertIssueMediaRel({
                    variables: {
                      issueId,
                      mediaId,
                    },
                  }),
                ),
              );

              logger.debug(
                `Successfully added ${newMediaIds.length} new media relations for issue ${issueId}`,
                {
                  component: 'IssuePage',
                },
              );
            } catch (error) {
              logger.error(
                `Failed to add media relations for issue ${issueId}`,
                {
                  error: error instanceof Error ? error.message : String(error),
                  component: 'IssuePage',
                },
              );
            }
          }

          if (refetchIssue) {
            await refetchIssue();
          }

          // Clear localStorage data after successful update
          clearLocalStorageData([ISSUE_FORM_DATA_KEY, ISSUE_EDIT_MODE_KEY]);

          // Remove mode=edit from the URL
          const url = new URL(window.location.href);
          url.searchParams.delete('mode');
          router.push(url.pathname + url.search);

          setPageState({ mode: 'viewing', viewer: 'owner' });
          return;
        }
      } catch (error) {
        logger.error('Error saving issue', {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          component: 'IssuePage',
        });
      }
    } else {
      // Switch to preview mode
      setPageState({
        mode: 'previewing',
        from: pageState.mode === 'creating' ? 'creating' : 'editing',
      });
    }
  }, [
    createIssue,
    displayLeader.displayNameShort,
    entityId,
    formData,
    getIsPreviewMode,
    getIsViewMode,
    handleVoteWithMe,
    influenceTargets,
    insertIssueMediaRel,
    insertMedia,
    isPreviewModeFromCreating,
    isViewModeAsOwner,
    isViewModeAsSupporter,
    issue?.issueMediaRels,
    issueId,
    issueUrl,
    mapToInfluenceTargets,
    pageState,
    refetchIssue,
    refetchSupporterCheck,
    router,
    secondaryTitle,
    setPageState,
    unvoteFromIssue,
    updateIssue,
  ]);

  const handleLoginComplete = useCallback(async () => {
    if (entityId) {
      // First check if the user is the owner
      if (issue && entityId === issue?.leader.id) {
        setPageState({ mode: 'viewing', viewer: 'owner' });
        return;
      }

      // Set state to show boost sway modal only if they aren't the owner
      setPageState({
        mode: 'viewing',
        viewer: 'supporter',
        modal: 'boostSway',
      });

      try {
        // Automatically vote with the user after login
        // Pass false to prevent handleVoteWithMe from setting the page state again
        await handleVoteWithMe(false);
        // Refetch data to make sure we have the latest supporter status
        await refetchSupporterCheck();
        if (refetchIssue) {
          await refetchIssue();
        }
      } catch (error) {
        logger.error('Error voting for issue after login', {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          component: 'IssuePage',
        });
      }
    }
  }, [
    entityId,
    handleVoteWithMe,
    setPageState,
    refetchSupporterCheck,
    refetchIssue,
    issue,
  ]);

  const handleFormChange = useCallback(
    (field: string, value: string) => {
      const updatedFormData = { ...formData, [field]: value, influenceTargets };
      setFormData(updatedFormData);

      // Save to localStorage when in create or edit mode
      if (getIsCreateMode() || getIsFullEditMode()) {
        saveToLocalStorage(ISSUE_FORM_DATA_KEY, updatedFormData);
      }

      if (field === 'topic') {
        if (value !== '') {
          setPageState({ mode: 'creating', hasTopic: true });
        } else {
          setPageState({ mode: 'creating', hasTopic: false });
        }
      }
    },
    [
      formData,
      setPageState,
      getIsCreateMode,
      getIsFullEditMode,
      influenceTargets,
    ],
  );

  const handleIssueImagesUpdate = useCallback(
    (urls: string[]) => {
      const updatedFormData = {
        ...formData,
        imageUrls: urls,
        influenceTargets,
      };
      setFormData(updatedFormData);

      // Save to localStorage when in create or edit mode
      if (getIsCreateMode() || getIsFullEditMode()) {
        saveToLocalStorage(ISSUE_FORM_DATA_KEY, updatedFormData);
      }
    },
    [formData, getIsCreateMode, getIsFullEditMode, influenceTargets],
  );

  const { scrollIntoView, targetRef } = useScrollIntoView<HTMLDivElement>({
    offset: 60,
  });

  const onGatherComplete = useCallback(() => {
    setPublishing(false);
    if (pendingRoute) {
      router.push(pendingRoute);
    }
  }, [pendingRoute, router, setPublishing]);

  const onMoveComplete = useCallback(() => {
    setSupporting(false);
  }, [setSupporting]);

  if (getIsViewMode() && !issue) return <ErrorPage />;

  if (publishing) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          background: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Animate
          animationData={gatherAnimation}
          loop={false}
          width="500px"
          height="500px"
          onComplete={onGatherComplete}
        />
      </div>
    );
  }

  return (
    <ColumnarLayout
      columnStartIsHiddenOnMobile={getIsVoteMode()}
      columnStart={
        <Stack
          gap="xl"
          h="100%"
          justify="start"
        >
          {!getIsViewMode() && (
            <div
              className={styles.backArrow}
              onClick={handleBackButton}
            >
              <ChevronLeft className={styles.backArrowIcon} /> Back
            </div>
          )}

          {getIsViewMode() && !issue?.isActive && (
            <Warning error="This issue is currently hidden from public view." />
          )}

          <IssueHeader
            formData={formData}
            issue={issue}
            displayLeader={displayLeader}
            handleFormChange={handleFormChange}
            verifiedVotersCount={issue?.verifiedSupporterCount || 0}
          />

          <Stack gap="lg">
            <IssueLeaderProfile
              displayLeader={displayLeader}
              handleEditIssue={handleEditIssue}
              handleSetIssueInactive={handleSetIssueInactive}
              isIssueActive={issue?.isActive || false}
            />

            {!getIsVoteMode() && (
              <IssueActionButtons
                isDiscouraged={isViewModeAsSupporter(pageState)}
                buttonText={buttonText}
                handleAction={handleAction}
              />
            )}
          </Stack>
        </Stack>
      }
      columnEnd={
        <Stack
          gap="xl"
          justify="start"
        >
          <>
            <LoginModal
              isShowing={isViewModeWithLoginModal(pageState)}
              onLoggedIn={handleLoginComplete}
            />
            {supporting && (
              <Animate
                animationData={moveAnimation}
                loop={false}
                width="350px"
                height="350px"
                onComplete={onMoveComplete}
              />
            )}
            <BoostSwayModal
              isShowing={isViewModeWithBoostSwayModal(pageState) && !supporting}
              leaderName={displayLeader?.displayNameShort || ''}
              topicName={formData.topic}
              personId={personId || ''}
              isVerified={isCurrentUserVerified}
              refetchIssue={refetchIssue}
              onClose={() => {
                setPageState({ mode: 'viewing', viewer: 'supporter' });
              }}
            />
          </>

          {!getIsVoteMode() && (
            <>
              {!getIsCreateMode() && !getIsFullEditMode() && (
                <Space
                  visibleFrom="md"
                  h="calc(100vh - var(--app-shell-header-height, 0px) - var(--app-shell-footer-height, 0px) - 24*var(--mantine-spacing-md))"
                />
              )}
              <Stack gap="md">
                {!getIsCreateMode() && !getIsFullEditMode() && (
                  <Stack
                    align="center"
                    justify="center"
                  >
                    <Popover
                      opened={isCreateModeWithRequiredInfo(pageState)}
                      middlewares={{ flip: false, shift: false }}
                      position="top"
                      withArrow
                      arrowOffset={10}
                      arrowSize={15}
                      width={300}
                      classNames={{
                        dropdown: styles.dropdown,
                      }}
                    >
                      <Popover.Target>
                        <ArrowDropDown
                          className={styles.bouncingArrow}
                          onClick={() =>
                            scrollIntoView({
                              alignment: 'center',
                            })
                          }
                        />
                      </Popover.Target>
                      <Popover.Dropdown>
                        <Text
                          size="md"
                          fw={500}
                        >
                          Fill out details below to help your audience better
                          understand your topic, where it matters, and what
                          offices it impacts!
                        </Text>
                      </Popover.Dropdown>
                    </Popover>
                  </Stack>
                )}
                <div className={styles.divider} />

                <IssuePageInformationSection
                  formData={formData}
                  handleFormChange={handleFormChange}
                  handleIssueImagesUpdate={handleIssueImagesUpdate}
                  targetRef={targetRef}
                  entityId={entityId || undefined}
                  issueId={issueId}
                  issueMediaRels={issue?.issueMediaRels}
                />

                <div className={styles.divider} />
                <IssuePageInfluenceTargetSection
                  leaderName={displayLeader?.displayNameShort || ''}
                  influenceTargets={influenceTargets}
                  setInfluenceTargets={(newTargets) => {
                    {
                      /*hacky, fix later */
                    }
                    const targets =
                      typeof newTargets === 'function'
                        ? newTargets(influenceTargets)
                        : newTargets;
                    const updatedFormData = {
                      ...formData,
                      influenceTargets: targets,
                    };
                    setFormData(updatedFormData);
                    saveToLocalStorage(ISSUE_FORM_DATA_KEY, updatedFormData);
                  }}
                  issueHasInfluenceTargets={issueHasInfluenceTargets}
                  issueInfluenceTargetRels={issue?.issueInfluenceTargetRels}
                  styles={styles}
                />
              </Stack>
            </>
          )}
        </Stack>
      }
    />
  );
};

export { IssuePageContent };
